import { ChangeDetectorRef, Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { Constants } from '@frontendlib/common/types/Constants';
import { Stitch } from '@modules/common/types/stitch';
import { ConnectionsService } from '@modules/connections/services/connections.service';
import { Connection } from '@modules/connections/types/connection';
import type { ConnectionsManageListState } from '@modules/connections/types/connections-manage-list-state';
import { ConnectionsNeighboursFilters } from '@modules/connections/types/neighbours-filters';
import { RelevanceConnection } from '@modules/connections/types/relevance-connection';
import { KnotsService } from '@modules/knots/services/knots.service';
import { Knot } from '@modules/knots/types/knot';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { resolveNeighborMarkers } from '@modules/knowledge/utils/resolve-neighbor-markers';
import { StateKey } from '@modules/settings/types/state-key';
import { TagsService } from '@modules/tags/services/tags.service';
import { Tag } from '@modules/tags/types/tag';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { of, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-connections-knowledge-section',
  templateUrl: './connections-section.component.html',
  standalone: false,
  host: {
    class: 'flex h-full flex-col overflow-hidden px-8 py-4',
  },
})
export class KnowledgeConnectionsSectionComponent implements OnInit, OnDestroy {
  private readonly filterIndicator = viewChild(ContextMenuStateIndicatorComponent);
  public StitchType = StitchType;
  public StateKey = StateKey;
  public state: ConnectionsManageListState;
  public neighborsConnections: Connection[] = [];
  public stitchItem: Stitch;
  public pagesCount = 0;
  /** How many related connections there are in all, as the server counts them. */
  public total = 0;
  public page = 0;
  public perPage = Constants.PAGE_SIZE;
  public knotsHistory: Knot[] = [];
  public tagsHistory: Tag[] = [];
  public connectionsHistory: Connection[] = [];
  protected loading = false;

  private alive = new Subject<void>();
  private loadNeighbors = new Subject<void>();

  protected readonly defaultFiltersState: ConnectionsManageListState = {
    sort: {
      by: 'relevance',
      order: 'desc',
    },
    filters: {
      createdFrom: null,
      createdTo: null,
    },
  };

  constructor(
    private kpService: KnowledgePanelService,
    private connectionsService: ConnectionsService,
    private knotsService: KnotsService,
    private tagsService: TagsService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loadNeighbors
      .pipe(
        debounceTime(300),
        map(() => {
          const hasSomeHistory = this.knotsHistory.length || this.tagsHistory.length || this.connectionsHistory.length;
          // When no selection, pass empty arrays — the backend resolves all markers
          // from the source item (contextItems). This decouples the KP from form pagination.
          return [
            hasSomeHistory ? this.knotsHistory : [],
            hasSomeHistory ? this.tagsHistory : [],
            hasSomeHistory ? this.connectionsHistory : [],
          ] as const;
        }),
        switchMap(([knots, tags, connections]) => {
          if (!knots.length && !tags.length && !connections.length && !this.stitchItem?.id) {
            return of({ connections: [], count: 0 });
          }
          let afterConnection: RelevanceConnection | null = null;
          if (this.page > 0) {
            afterConnection = this.neighborsConnections.at(-1) as RelevanceConnection;
          }
          const config = { requestContext: owningTenantContext(this.stitchItem?.accountId) };
          const lookups = { knots: this.knotsService, tags: this.tagsService, connections: this.connectionsService };

          return resolveNeighborMarkers({ knots, tags, connections }, lookups, config).pipe(
            switchMap((markers) =>
              markers
                ? this.connectionsService.getNeighbors(
                    {
                      ...ConnectionsNeighboursFilters.fromListState(this.state),
                      ...markers,
                      contextItems: this.stitchItem?.id ? [this.stitchItem.id] : [],
                      limit: this.perPage,
                      after: afterConnection,
                    },
                    config,
                  )
                : of({ connections: [], count: 0 }),
            ),
          );
        }),
        catchError((e) => {
          this.loading = false;
          return throwError(() => e);
        }),
        takeUntil(this.alive),
      )
      .subscribe(({ connections, count }) => {
        this.loading = false;
        this.pagesCount = Math.ceil(count / this.perPage);
        this.total = count;
        this.neighborsConnections = [
          ...this.neighborsConnections.slice(0, this.page * this.perPage),
          ...connections,
          ...this.neighborsConnections.slice((this.page + 1) * this.perPage, count),
        ].filter((i) => !!i);
      });

    // The CONTEXT item — what the 3rd panel has open — not this panel's own form item. It is the
    // sole input of the neighbour query when nothing is selected (`contextItems` below), so reading
    // the DP's `selectedStitchItem` here left the list empty for good once a DP form had been closed
    // and nulled it. See SBUX-750 / devdoc/specs/knowledge-panel-selection-scopes.md.
    this.kpService
      .getContextStitchItem()
      .pipe(takeUntil(this.alive))
      .subscribe((stitchItem) => {
        // Closing the item in the 3rd panel clears this row's sort & filter, as its × does, so the next item starts
        // from the default (Johanna, 2026-10-21: a filter set for one item stayed on after it closed).
        if (this.stitchItem && !stitchItem) {
          this.filterIndicator()?.reset();
        }
        this.stitchItem = stitchItem;
        // Refetch: the query is keyed on this item, so the reload must not depend on a history
        // emission happening to arrive with the item change.
        this.reloadNeighbors();
      });

    // Everything this panel's queries must honour. It lives on the service so all four lists in the
    // panel share it.
    this.kpService
      .getQueryFilter()
      .pipe(takeUntil(this.alive))
      .subscribe(({ connections, knots, tags }) => {
        this.knotsHistory = knots;
        this.tagsHistory = tags;
        this.connectionsHistory = connections;
        this.reloadNeighbors();
        this.changeDetector.detectChanges();
      });
  }

  ngOnDestroy() {
    this.kpService.setDeepMode('connections', false);
    this.loadNeighbors.complete();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleClickItem(connection: Connection) {
    this.kpService.addConnectionToSelection(connection, null, false);
  }

  /** The related connections not shown yet: the "+N more" pill's count. */
  get remaining(): number {
    return Math.max(0, this.total - (this.page + 1) * this.perPage);
  }

  showMore() {
    this.page = Math.min(this.page + 1, this.pagesCount - 1);
    if (!this.neighborsConnections[this.page * this.perPage]) {
      this.loading = true;
      this.loadNeighbors.next();
    }
  }

  showLess() {
    this.page = 0;
  }

  setState(state: ConnectionsManageListState) {
    this.state = state;
    this.kpService.setDeepMode('connections', !!state.filters?.deepMode);
    this.reloadNeighbors();
  }

  private reloadNeighbors(): void {
    this.page = 0;
    this.neighborsConnections = [];
    this.loading = true;
    this.loadNeighbors.next();
  }
}
